import { Grid, IconButton, TextField } from '@mui/material';
import React, { useState } from 'react';
import { Field, FieldProps } from 'formik';
import moment from 'moment';
import { Clear } from '@mui/icons-material';
import Button from '@mui/material/Button';
import Add from '@mui/icons-material/Add';
import { UseAuthPermissions } from '../../session/UseAuthPermissions';
import { AccountData, HolidayCalendar } from '../../api';
import { getTimeFormat } from '../../common/getDateAndTimeFormat';
import { useAuth } from '../../session/InternalAuthProvider';
import SharedMobileTimePicker from '../../common/ui/SharedMobileTimePicker';

type workingHoursProps = {
  isSubmitting: boolean;
  fieldValueDayIndex?: string;
  initialStartSeconds?: number[];
};

export const WorkingHoursFixed = ({ isSubmitting, fieldValueDayIndex, initialStartSeconds }: workingHoursProps) => {
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;

  const { readOnly } = UseAuthPermissions();
  const [deletedFixedValues, setDeletedFixedValues] = useState<number[]>([]);
  const [addingNewTime, setAddingNewTime] = useState(false);
  const [newTime, setNewTime] = useState(moment().startOf('day'));
  const formIsDisabled = readOnly || isSubmitting;
  const timeFormat = getTimeFormat(account);
  return (
    <Grid display="flex" gap={1}>
      <Field name={fieldValueDayIndex ? `${fieldValueDayIndex}.start_seconds` : 'start_seconds'}>
        {({ field, form }: FieldProps<number[]>) => {
          const deleted = initialStartSeconds?.filter((item) => !field.value.includes(item));
          // it wont be always true, but faster for now
          if (deleted && deleted?.length !== deletedFixedValues.length) {
            setDeletedFixedValues(deleted);
          }
          return (
            <Grid container gap={2}>
              <Grid item>
                {addingNewTime ? (
                  <SharedMobileTimePicker
                    sx={{ width: '100px' }}
                    value={newTime}
                    minutesStep={15}
                    onChange={(newValue: moment.Moment | null) => setNewTime(newValue!)}
                    onAccept={(newValue: moment.Moment | null) => {
                      void form.setFieldValue(
                        field.name,
                        [...field.value, newValue!.hours() * 3600 + newValue!.minutes() * 60].sort((a, b) => b - a),
                      );
                      setAddingNewTime(false);
                      setNewTime(moment().startOf('day'));
                    }}
                  />
                ) : (
                  <Button
                    disabled={formIsDisabled}
                    onClick={() => {
                      setAddingNewTime(true);
                    }}
                    variant="outlined"
                    color="inherit"
                    sx={{ height: '44px' }}>
                    <Add />
                  </Button>
                )}
              </Grid>
              {[...field.value, ...deletedFixedValues]
                ?.sort((a, b) => a - b)
                .map((x) => {
                  const deletedValue = deletedFixedValues.find((val) => val === x);
                  const addedValue =
                    field.value.find((val) => val === x) && !initialStartSeconds?.find((val) => val === x);
                  return (
                    <Grid item>
                      <TextField
                        disabled
                        sx={{
                          border: deletedValue || addedValue ? '2px solid' : undefined,
                          borderColor: deletedValue ? 'error.main' : addedValue ? 'primary.main' : undefined,
                          borderRadius: '6px',
                          width: deletedValue
                            ? '90px'
                            : account.holiday_calendar === HolidayCalendar.US
                            ? '120px'
                            : '110px',
                          '&& .MuiInputBase-root': {
                            paddingInlineStart: '0px',
                          },
                        }}
                        size="small"
                        value={moment().startOf('day').add(x, 'seconds').format(timeFormat)}
                        variant="outlined"
                        InputProps={{
                          endAdornment: deletedValue ? undefined : (
                            <IconButton
                              disabled={formIsDisabled}
                              onClick={() => {
                                setDeletedFixedValues([...deletedFixedValues, x]);
                                void form.setFieldValue(
                                  field.name,
                                  field.value.filter((val) => val !== x).sort((a, b) => b - a),
                                );
                              }}
                              size="small">
                              <Clear />
                            </IconButton>
                          ),
                          sx: {
                            paddingInlineEnd: 0,
                            color: 'text.primary',
                            '& .MuiInputAdornment-positionEnd': {
                              marginInlineStart: 0,
                            },
                          },
                        }}
                      />
                    </Grid>
                  );
                })}
            </Grid>
          );
        }}
      </Field>
    </Grid>
  );
};
